import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { ago, Chip, errorMessage, Page } from "@/components/ui-bits";
import { authClient } from "@/lib/auth-client";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/admin")({
  component: Admin,
});

const joined = (ts: string | number | Date) => new Date(ts).toLocaleDateString([], { day: "numeric", month: "short" });
const TH = "px-[22px] py-3 font-medium";

function Admin() {
  const { data: session } = authClient.useSession();
  const users = useQuery(orpc.admin.users.queryOptions());
  const [search, setSearch] = useState("");
  const act = useMutation({
    mutationFn: ({ op, userId }: { op: "approve" | "revoke" | "reject"; userId: string }) => client.admin[op]({ userId }),
    onSuccess: () => users.refetch(),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const mail = useMutation({
    mutationFn: () => client.admin.testEmail(),
    onSuccess: (r) => toast.success(`Sent to ${r.to}; check the inbox (and spam)`),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const actions = (
    <Button variant="outline" disabled={mail.isPending} onClick={() => mail.mutate()}>
      {mail.isPending ? "Sending…" : "Send test email"}
    </Button>
  );
  if (users.error)
    return (
      <Page title="Admin" width="max-w-[1000px]" actions={actions}>
        <p className="m-0 text-[#F48A80]">{users.error.message}</p>
      </Page>
    );

  const pending = users.data?.filter((u) => !u.approved && u.role !== "admin") ?? [];
  const active = users.data?.filter((u) => u.approved || u.role === "admin") ?? [];
  const q = search.trim().toLowerCase();
  const shown = q ? active.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q)) : active;

  return (
    <Page title="Admin" width="max-w-[1000px]" actions={actions}>
      <section aria-labelledby="ad-wait" className="rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#3A3935]">
        <h2 id="ad-wait" className="m-0 px-[22px] pt-[18px] pb-2.5 font-mono text-[15px] font-bold">
          Waiting for approval {pending.length > 0 && <span className="text-[#FF4F1A]">{pending.length}</span>}
        </h2>
        {users.data && pending.length === 0 && (
          <p className="m-0 border-t border-[#2A2927] px-[22px] py-3.5 text-[#A8A59D]">Nobody is waiting.</p>
        )}
        {pending.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-t border-[#2A2927] px-[22px] py-3.5">
            <span className="flex min-w-0 flex-[1_1_280px] flex-col">
              <span className="font-medium">{u.name}</span>
              <span className="text-[13px] text-[#A8A59D]">
                {u.email} · asked {ago(u.createdAt)}
              </span>
            </span>
            <Button disabled={act.isPending} onClick={() => act.mutate({ op: "approve", userId: u.id })}>
              Approve
            </Button>
            <Button
              variant="ghost"
              className="px-3.5 text-[#F48A80] hover:bg-[rgba(240,100,90,0.1)] hover:text-[#F48A80]"
              disabled={act.isPending}
              onClick={() => window.confirm(`Delete the request from ${u.email}?`) && act.mutate({ op: "reject", userId: u.id })}
            >
              Reject
            </Button>
          </div>
        ))}
      </section>

      <section aria-labelledby="ad-users" className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <h2 id="ad-users" className="m-0 font-mono text-[15px] font-bold">
            Users <span className="text-[#A8A59D]">{active.length}</span>
          </h2>
          <label className="flex">
            <span className="sr-only">Search users</span>
            <Input
              type="search"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[260px] max-w-full bg-[#1A1A19]"
            />
          </label>
        </div>
        <div className="overflow-x-auto rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
                <th scope="col" className={TH}>
                  Person
                </th>
                <th scope="col" className={TH}>
                  Role
                </th>
                <th scope="col" className={TH}>
                  Joined
                </th>
                <th scope="col" className={TH}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => {
                const you = u.id === session?.user.id;
                return (
                  <tr key={u.id} className="border-t border-[#2A2927]">
                    <td className="px-[22px] py-3">
                      <span className="font-medium">{u.name}</span>
                      <br />
                      <span className="text-[13px] text-[#A8A59D]">
                        {u.email}
                        {you && " · you"}
                      </span>
                    </td>
                    <td className="px-[22px] py-3">
                      {u.banned ? (
                        <Chip value="error" label="banned" />
                      ) : u.role === "admin" ? (
                        <Chip value="hot" label="admin" />
                      ) : (
                        <Chip value="neutral" label="user" />
                      )}
                    </td>
                    <td className="px-[22px] py-3 text-[#A8A59D]">{joined(u.createdAt)}</td>
                    <td className="px-[22px] py-3 text-right">
                      {u.role !== "admin" && !you && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={act.isPending}
                          onClick={() =>
                            window.confirm(`Revoke ${u.email}? They are signed out and can't sign in again until approved.`) &&
                            act.mutate({ op: "revoke", userId: u.id })
                          }
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.data && shown.length === 0 && (
                <tr className="border-t border-[#2A2927]">
                  <td colSpan={4} className="px-[22px] py-3 text-[#A8A59D]">
                    {q ? "Nobody matches that search." : "No users yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Page>
  );
}
